import { useMemo, useState } from 'react'
import { Blocks, Eye, LockKeyhole, Search, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useStudioLocale } from '@/i18n'
import { cn } from '@/lib/utils'
import { discover, recommend, type UiBlockRef } from './ui-block-consumer'

interface UiBlockWorkbenchProps {
  selectedPreview: UiBlockRef | null
  onPreview: (ref: UiBlockRef) => void
}

const INTENT_SHORTCUTS = [
  { key: 'dashboard', query: 'dashboard analytics' },
  { key: 'store', query: 'store ecommerce' },
  { key: 'landing', query: 'landing hero' },
  { key: 'auth', query: 'login auth' },
] as const

function readableBlockName(id: string): string {
  return id
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function UiBlockWorkbench({ selectedPreview, onPreview }: UiBlockWorkbenchProps) {
  const { locale, strings } = useStudioLocale()
  const copy = strings.designSystem.uiBlocks
  const showCatalogProse = locale === 'en'
  const [intent, setIntent] = useState('')
  const [recommendationIntent, setRecommendationIntent] = useState('')
  const discovery = useMemo(() => discover(), [])
  const recommendations = useMemo(
    () => (recommendationIntent.trim()
      ? recommend({ intent: recommendationIntent, limit: 3 })
      : []),
    [recommendationIntent],
  )

  return (
    <section
      data-testid="ui-block-workbench"
      aria-labelledby="ui-block-workbench-title"
      className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(260px,0.9fr)_minmax(0,1.5fr)] lg:p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <Sparkles className="h-4 w-4" aria-hidden />
            {copy.eyebrow}
          </div>
          <h2 id="ui-block-workbench-title" className="mt-2 text-lg font-semibold tracking-tight text-foreground">
            {copy.title}
          </h2>
          <p className="mt-1 max-w-xl text-sm leading-6 text-muted-foreground">
            {copy.description}
          </p>

          <label htmlFor="ui-block-intent" className="mt-4 block text-xs font-medium text-foreground">
            {copy.intentLabel}
          </label>
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              id="ui-block-intent"
              value={intent}
              onChange={(event) => {
                setIntent(event.target.value)
                setRecommendationIntent(event.target.value)
              }}
              placeholder={copy.intentPlaceholder}
              className="h-11 pl-9"
            />
          </div>

          <div className="mt-3" aria-label={copy.shortcutsLabel}>
            <p className="sr-only">{copy.shortcutsLabel}</p>
            <div className="flex flex-wrap gap-2">
              {INTENT_SHORTCUTS.map((shortcut) => (
                <button
                  key={shortcut.key}
                  type="button"
                  onClick={() => {
                    setIntent(copy.shortcuts[shortcut.key])
                    setRecommendationIntent(shortcut.query)
                  }}
                  className={cn(
                    'inline-flex min-h-11 cursor-pointer items-center rounded-full border px-3 text-xs font-medium transition-colors',
                    recommendationIntent === shortcut.query
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                  )}
                >
                  {copy.shortcuts[shortcut.key]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="min-w-0 rounded-lg border border-border bg-background/60 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Blocks className="h-4 w-4 text-muted-foreground" aria-hidden />
              <h3 className="text-sm font-semibold text-foreground">{copy.recommendations}</h3>
            </div>
            <Badge variant="outline" className="text-muted-foreground">
              {copy.admittedCount(discovery.admittedCount)}
            </Badge>
          </div>

          {recommendationIntent.trim() && recommendations.length === 0 ? (
            <p className="mt-3 rounded-lg border border-dashed border-border px-3 py-4 text-sm leading-6 text-muted-foreground">
              {copy.noMatches}
            </p>
          ) : null}

          {!recommendationIntent.trim() ? (
            <div className="mt-3 flex min-h-32 items-center justify-center rounded-lg border border-dashed border-border px-4 text-center text-sm leading-6 text-muted-foreground">
              {copy.shortcutsLabel}
            </div>
          ) : (
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {recommendations.map((block, index) => {
                const selected =
                  selectedPreview?.category === block.category &&
                  selectedPreview.blockId === block.blockId

                return (
                  <button
                    key={`${block.category}/${block.blockId}`}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onPreview({ category: block.category, blockId: block.blockId })}
                    className={cn(
                      'group flex min-h-52 cursor-pointer flex-col rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/40 hover:bg-accent/40',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="secondary" className="max-w-full truncate">
                        {showCatalogProse ? block.categoryLabel : copy.readOnly}
                      </Badge>
                      <Eye className={cn('h-4 w-4 shrink-0', selected ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
                    </div>
                    <span className="mt-2 text-sm font-semibold text-foreground">
                      {showCatalogProse
                        ? readableBlockName(block.id)
                        : `${copy.recommendations} ${index + 1}`}
                    </span>
                    <code className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {block.category}/{block.id}
                    </code>
                    <span className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                      {showCatalogProse ? block.description : copy.preview}
                    </span>
                    {showCatalogProse ? (
                      <span className="mt-2 flex flex-wrap gap-1" aria-label={block.tags.join(', ')}>
                        {block.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                            {tag}
                          </span>
                        ))}
                      </span>
                    ) : null}
                    <span className="mt-auto pt-3 text-xs font-medium text-primary">
                      {selected ? copy.selectedPreview : copy.preview}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-2 border-t border-border bg-muted/30 px-4 py-3 text-xs leading-5 text-muted-foreground lg:px-5">
        <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="font-medium text-foreground">{copy.readOnly}</span>
        <span>{copy.authorityUnavailable}</span>
      </div>
    </section>
  )
}
